/**
 * ChatPage
 *
 * Full-page chat interface that reuses the Chat component with the session sidebar visible.
 * Supports deep-linking to specific sessions via /chat/:sessionId URLs for easy sharing.
 *
 * Features:
 * - Full-height layout for chat interface
 * - Session sidebar available for quick session switching
* - New chat sessions with timestamp format: chat-{mm-dd-yy hh:mm:ss}
 * - Edit/delete buttons aligned to right of session titles
 * - No duplication of sidebar logic
 *
 * Changes:
 * - Recovers stale session deep links by replacing them with the initialized active session URL
 * - Updated to reuse Chat component with default session sidebar behavior and URL-based session selection
 * - Removed duplicate sidebar implementation
 * - Simplified to single-component render
 * - Sessions now sorted by most recent update first
 * - Enhanced session title layout with right-aligned action buttons
 * - New chat title format includes timestamp for easy identification
 */

import React, { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';
import Chat from './Chat';

const ChatPage: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId?: string }>();

  const initialSessionId = sessionId && /^\d+$/.test(sessionId)
    ? Number(sessionId)
    : undefined;

  const handleSessionChange = useCallback((
    newSessionId: number,
    options?: { replace?: boolean }
  ) => {
    if (!newSessionId) return;

    const sessionIdString = String(newSessionId);
    if (sessionIdString === sessionId) {
      return;
    }

    const shouldReplace = options?.replace ?? !sessionId;
    navigate(`/chat/${sessionIdString}`, { replace: shouldReplace });
  }, [navigate, sessionId]);

  return (
    <div className="h-full">
      <Chat
        initialSessionId={initialSessionId}
        onSessionChange={handleSessionChange}
        isFullPage
      />
    </div>
  );
};

export default ChatPage;
